import React, { useEffect, useState } from "react";
import { Box, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { notifyError } from "../../../utils/notify";
import { ClientGroup, RuleTree } from "../../../types/clientGroups";
import { useClientGroups } from "../../../hooks/useClientGroups";
import SentenceRuleBuilder from "./SentenceRuleBuilder";
import { emptyRuleTree, normalizeTree } from "./ruleModel";

interface Props {
  opened: boolean;
  group: ClientGroup | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Edit Rules modal (design §5.4) — the same sentence builder in a centred
 *  modal, with the count strip below the conditions. */
export default function EditRulesModal({
  opened,
  group,
  onClose,
  onSaved,
}: Props) {
  const { updateGroup } = useClientGroups();
  const [rules, setRules] = useState<RuleTree>(emptyRuleTree());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opened && group)
      setRules(group.rules ? normalizeTree(group.rules) : emptyRuleTree());
  }, [opened, group]);

  const save = async () => {
    if (!group) return;
    setSaving(true);
    try {
      await updateGroup(group.id, { rules });
      onSaved();
      onClose();
    } catch (e: any) {
      notifyError(e?.message || "Could not save rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={660}
      title={
        <Box>
          <Text fw={700} size="17px">
            Edit Rules
          </Text>
          <Text size="13px" c="dimmed">
            Clients who match these conditions are in the group.
          </Text>
        </Box>
      }
    >
      <Stack gap="md">
        <SentenceRuleBuilder
          value={rules}
          onChange={setRules}
          countPlacement="bottom"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="var(--brand-teal)" loading={saving} onClick={save}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
